import type { ReactNode } from "react";

import { cn } from "../cn";

export interface KeyValuePair {
  name: string;
  value: ReactNode;
}

export interface KeyValueGridProps {
  pairs: KeyValuePair[];
  /** Names the set for a screen reader — "everything this event carried". */
  label?: string;
  className?: string;
}

/**
 * What something carried, as flat pairs rather than as a wall of chips.
 *
 * Two screens open onto the same shape: the payload Live events truncates on
 * the row, and the attributes a step on Requests recorded. Requests drew a
 * two-column `dl`, Live events drew nothing — it rendered every field as a
 * `Badge` on the row instead, which is how a real purchase event came to paint
 * eleven pills across three lines and drown the outcome beside them.
 *
 * The columns are `auto-fill`, not a fixed count. These lists are two pairs
 * sometimes and twenty others, and a fixed two-column grid leaves half the
 * width empty for the short case and wraps a key away from its value in the
 * long one. `minmax(215px, 1fr)` is the narrowest a mono key and a mono value
 * read side by side before the value starts eliding immediately.
 *
 * The value elides rather than wraps. A pair that takes three lines breaks the
 * row rhythm the grid exists to hold, and the full value is one click away on
 * both screens that use this — Requests through the step's own record, Live
 * events through the request the event rode on.
 *
 * Inside a column the key is a fixed `7rem` and the value follows it. The
 * obvious spelling is `justify-between`, which is what both screens had, and
 * on a wide panel it pushes the two halves of a pair 200px apart — so `kind`
 * and `internal` stop reading as one fact and start reading as two columns
 * that happen to be adjacent.
 */
export function KeyValueGrid({ pairs, label, className }: KeyValueGridProps) {
  return (
    <dl
      aria-label={label}
      className={cn(
        "grid grid-cols-[repeat(auto-fill,minmax(min(215px,100%),1fr))] gap-x-6 gap-y-0.5",
        "rounded-[var(--radius-sm)] border border-rule bg-sunk px-3.5 py-2.5",
        className,
      )}
    >
      {pairs.map((pair) => (
        <div
          key={pair.name}
          className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] items-baseline gap-x-3 py-0.5"
        >
          <dt className="truncate font-mono text-micro text-ink-3">{pair.name}</dt>
          <dd className="truncate font-mono text-micro text-ink-2">{pair.value}</dd>
        </div>
      ))}
    </dl>
  );
}
